import { useEffect, useCallback, useRef } from 'react'

const useScroll = <T extends HTMLElement>(adapter: (ref: T, position: number, lastCall: boolean) => void, maxPos: number = Infinity) => {
  const containerRef = useRef<T>()
  const flag = useRef<boolean>()

  const setPosition = useCallback(() => {
    if (containerRef.current && window.scrollY < maxPos) {
      const currentYPos = Math.round(window.pageYOffset)
      flag.current = false
      adapter(containerRef.current, currentYPos, flag.current)
    } else if (containerRef.current && window.scrollY >= maxPos && flag.current === false) {
      flag.current = true
      adapter(containerRef.current, maxPos, flag.current)
    }
  }, [adapter, maxPos])

  const raWrappedHandler = useCallback(
    (event: Event) => {
      window.requestAnimationFrame(setPosition)
      event.preventDefault()
    },
    [setPosition]
  )

  useEffect(() => {
    setPosition()
    window.addEventListener('scroll', raWrappedHandler)
    return () => {
      window.removeEventListener('scroll', raWrappedHandler)
    }
  }, [raWrappedHandler, setPosition])

  const refProxy = useCallback<(el: T | null) => void>((el) => {
    if (el) {
      containerRef.current = el
    }
  }, [])

  return [refProxy]
}

export default useScroll
